import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import intlLabels from './intlLabels';
import {
  Annotation,
  Amount,
  FhirArray,
  CodeableConcept,
  Coding,
  FhirDateTime,
  FhirUtcDateTime,
  FhirIdentifier,
  Period,
  Quantity,
  Range,
  Ratio,
  Timing,
  FhirBoolean,
  Code,
  FhirString,
  Integer,
  Time,
} from '../../../../../util/fhirResourceParsers/Types';
import FhirPropertyParser, {
  FhirPropertyParserGroup,
  APIResource,
} from '../../../../../util/fhirResourceParsers/FhirPropertyParser';
import {
  defaultMomentFormat,
  ConfiguredMomentFormat,
} from '../../../util/momentFormats';
import { BaseParserOptions } from '../../../../../util/fhirResourceParsers/SharedTypes';
import { Guid, MessageDescriptor, DataSourceType } from '../../../util/types';
import { dataSourceTypes } from '../../../util/configuredResourceTypes';
import {
  ParserSupportedFhirTypes,
  JmesPath,
} from '../../../../../util/JMESParser/jmesFhirTypes';

/**
 * Some parsing behavior is configuration and run time driven.  This class encapsulates some of the configuration bound parsing optiosn
 *
 * @export
 * @class ParseConfiguration
 */
export class ParseConfiguration {
  private _dateFormat: ConfiguredMomentFormat;

  constructor(dateFormat: ConfiguredMomentFormat = defaultMomentFormat) {
    this._dateFormat = dateFormat;
  }

  /**
   * The options for a FhirDateTime resource parser
   *
   * @readonly
   * @type {Object}
   * @memberof ParseConfiguration
   */
  get dateTimeOptions(): BaseParserOptions {
    return {
      fhirDateTime: {
        monthFormat: this._dateFormat,
        dayFormat: this._dateFormat,
        minuteFormat: this._dateFormat,
      },
    };
  }

  /**
   * The options for a Peirod resource parser
   *
   * @readonly
   * @type {Object}
   * @memberof ParseConfiguration
   */
  get periodOptions(): BaseParserOptions {
    const fhirDateTimeParser = new FhirDateTime(this.dateTimeOptions);
    return {
      period: {
        fhirDateTimeParser,
      },
    };
  }
}

export class Link {
  contentType: string;
  url: string;
  /** Base64 `Attachment.data` for documents that are not file-service files. */
  data?: string;
  title?: string;
  resourceId?: string;
  resourceType?: string;
  constructor(
    contetType: string,
    url: string,
    data?: string,
    title?: string,
    resourceId?: string,
    resourceType?: string,
  ) {
    this.contentType = contetType;
    this.url = url;
    this.data = data;
    this.title = title;
    this.resourceId = resourceId;
    this.resourceType = resourceType;
  }

  get isValid() {
    return Boolean(this.contentType && (this.url || this.data));
  }
}

export type ResourceAgnosticColumnKeyHash = {
  [key: string]: Guid;
};

const resourceAgnosticPropertyKeys: ResourceAgnosticColumnKeyHash = {
  jmesColumn: '88f766a2-e196-4474-98de-ed7b36c8c161',
};

export type PropertyConfigOptions = {
  defaultSort?: boolean;
  isLink?: boolean;
  priority?: number;
  isFavorite?: boolean;
};

/**
 * Parsing logic to extract a human-readable display value of a property from a fhir resource and associated metadata
 *
 * @export
 * @class PropertyConfig
 */
export class PropertyConfig {
  public readonly key: Guid;
  public staticLabel: MessageDescriptor;
  public createParserGroup: () => FhirPropertyParserGroup;
  public defaultSort: boolean;
  public isLink: boolean;
  private _disableSort: boolean;
  private _configuredLabel: string;
  private _instantiatedParserGroup: FhirPropertyParserGroup;
  private _jmesPath: JmesPath;
  private _jmesResultParser: ParserSupportedFhirTypes;
  public priority: number;
  public isFavorite: boolean;

  static initJMESParseColumn(
    jmesPath: JmesPath,
    jmesResultParser: ParserSupportedFhirTypes,
  ): PropertyConfig {
    const column = new PropertyConfig(
      resourceAgnosticPropertyKeys.jmesColumn,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
      null,
    );
    column.jmesPath = jmesPath;
    column.jmesResultParser = jmesResultParser;
    column.disableSort = true;
    return column;
  }

  /**
   * Creates an instance of Column.
   * @param {Guid} key - Guid to uniquely identify a configurable column
   * @param {MessageDescriptor} staticLabel - An intl message
   * @param {Function} createParserGroup - a function to lazy initialzie
   * @memberof Column
   */
  constructor(
    key: Guid,
    staticLabel: MessageDescriptor,
    createParserGroup: () => FhirPropertyParserGroup,
    options?: PropertyConfigOptions,
  ) {
    this.key = key;
    this.staticLabel = staticLabel;
    this.createParserGroup = createParserGroup;
    this.defaultSort = !!options?.defaultSort;
    this.isLink = !!options?.isLink;
    this._disableSort = !!options?.isLink;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._instantiatedParserGroup = null;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._jmesPath = null;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._jmesResultParser = null;
    this.priority = _isNil(options?.priority)
      ? Number.MAX_SAFE_INTEGER
      : // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        options.priority;
    this.isFavorite = !!options?.isFavorite;
  }

  set jmesPath(value: JmesPath) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._jmesPath = value || null;
  }

  get jmesPath() {
    return this._jmesPath;
  }

  set jmesResultParser(value: ParserSupportedFhirTypes) {
    this._jmesResultParser = value || null;
  }

  get jmesResultParser() {
    return this._jmesResultParser;
  }

  set configuredLabel(value: string) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._configuredLabel = value || null;
  }

  get configuredLabel() {
    return this._configuredLabel;
  }

  get parserGroup(): FhirPropertyParserGroup {
    if (this._instantiatedParserGroup === null) {
      this._instantiatedParserGroup = this.createParserGroup();
    }

    return this._instantiatedParserGroup;
  }

  get disableSort(): boolean {
    return this._disableSort;
  }

  set disableSort(value: boolean) {
    this._disableSort = !!value;
  }

  getLink(fhirResource: APIResource): Link {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.isLink) return null;

    const rawValue = this.parserGroup.getRawValue(fhirResource);
    const contentType = _get(rawValue, 'contentType');
    const url = _get(rawValue, 'url');

    return new Link(
      contentType,
      url,
      _get(rawValue, 'data'),
      _get(rawValue, 'title'),
      _get(fhirResource, 'resource.id'),
      _get(fhirResource, 'resource.resourceType'),
    );
  }
}

export type PropertyConfigKeyHash = {
  [key: string]: PropertyConfig;
};

export class PropertyConfigKeyMap {
  private _map: PropertyConfigKeyHash;
  constructor(propertyConfigs: PropertyConfig[] = []) {
    this._map = {};
    propertyConfigs.forEach(column => {
      this._map[column.key] = column;
    });
  }

  getPropertyConfig(key: string): PropertyConfig {
    return this._map[key];
  }
}

const observationPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '76915e11-e39c-4891-9adb-f5ff2b33c334',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    'c825fa11-6760-4015-97cb-aa2ddf8c8856',
    intlLabels.shared.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'da8cc9af-348d-455b-a79a-701621f23a7d',
    intlLabels.shared.category,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.category', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '630e576d-ef01-4f31-b9b5-2b3ec5bcffd7',
    intlLabels.shared.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.code', CodeableConcept),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    '911257ef-eb91-4c6f-b0b0-dbf1bc553f54',
    intlLabels.shared.time,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.effectiveDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirUtcDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.effectivePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    '20c6fd8c-6bec-42e2-a62a-134149c8fffa',
    intlLabels.shared.issued,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.issued',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    'b3d4b6a6-dc7b-436b-b034-4ae7007f41db',
    intlLabels.shared.value,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.valueQuantity', Quantity),
        new FhirPropertyParser(
          'resource.valueCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
        new FhirPropertyParser('resource.valueString', FhirString),
        new FhirPropertyParser('resource.valueBoolean', FhirBoolean),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.valueRange', Range),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.valueRatio', Ratio),
        new FhirPropertyParser(
          'resource.valueTime',
          Time,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.valueDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirUtcDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.valuePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.component', FhirArray, {
          fhirArray: {
            optionalPathParsers: [
              {
                arrayElementPath: 'valueQuantity',
                arrayElementParser: new Quantity(),
              },
              {
                arrayElementPath: 'valueCodeableConcept',
                arrayElementParser: new CodeableConcept(),
              },
              {
                arrayElementPath: 'valueString',
                arrayElementParser: new FhirString(),
              },
              {
                arrayElementPath: 'valueRange',
                arrayElementParser: new Range(),
              },
              {
                arrayElementPath: 'valueRatio',
                arrayElementParser: new Ratio(),
              },
              {
                arrayElementPath: 'valueTime',
                arrayElementParser: new FhirDateTime(
                  parseConfig.dateTimeOptions,
                ),
              },
              {
                arrayElementPath: 'valueDateTime',
                arrayElementParser: new FhirDateTime(
                  parseConfig.dateTimeOptions,
                ),
              },
              {
                arrayElementPath: 'valuePeriod',
                arrayElementParser: new Period(parseConfig.periodOptions),
              },
            ],
            concatenate: true,
          },
        }),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'c4cb0ff2-2dcf-45d9-ae54-254d16f1db85',
    intlLabels.shared.dataAbsentReason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dataAbsentReason', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'dd5cfc05-1475-421d-b0e2-e105aa4943a6',
    intlLabels.shared.interpretation,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.interpretation', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '7e3753ae-2c60-4c48-9a0a-7aa4283f2ba9',
    intlLabels.shared.comment,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.comment', FhirString),
      ]),
  ),
  new PropertyConfig(
    '005f1d9b-2205-4a4b-b8ff-69fb38a9b392',
    intlLabels.shared.bodySite,
    () => new FhirPropertyParserGroup([]),
  ),
  new PropertyConfig(
    'a4718ba3-b763-4768-baa9-44ffeb195fc5',
    intlLabels.shared.method,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.method', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'c8e2e80c-4d80-4827-9b81-2a597108c741',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const procedurePropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    'd4898306-7252-4a1e-bd88-50ff5bd173c',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '1defd333-4424-4bfb-b281-ddd4f8ea5a9b',
    intlLabels.shared.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '09deba41-a8bd-41d4-9270-53ac5e38249b',
    intlLabels.procedure.notDone,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.notDone', FhirBoolean),
      ]),
  ),
  new PropertyConfig(
    'cfa0014a-4e5d-4d52-9be9-b608640031f3',
    intlLabels.procedure.notDoneReason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.notDoneReason', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '682c75af-6384-4d2e-a625-f01a3000b08a',
    intlLabels.shared.category,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.category', CodeableConcept),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '043848ee-da16-40fa-ae83-a62296c54bac',
    intlLabels.shared.code,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.code', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'f166fbc7-b41c-49ec-809c-534c81ed985e',
    intlLabels.shared.time,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.performedDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.performedPeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    'f71ea23f-09c5-4ac6-9472-ef9231e16aeb',
    intlLabels.shared.reason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '52744f6f-5ec2-477d-98cb-5ce29421c285',
    intlLabels.shared.bodySite,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.bodySite', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '2e40b1d8-c329-4761-9ca6-a496194e6aa8',
    intlLabels.shared.outcome,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.outcome', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'd2a268bb-f5f7-4082-941f-403d1187dd4e',
    intlLabels.shared.complication,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.complication', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'bc0e3e52-a294-4357-8552-bab98b5764a0',
    intlLabels.shared.followUp,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.followUp', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'af8735ed-1b0d-4ceb-8423-a7eeb80ad867',
    intlLabels.shared.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'a2033abe-5414-41d6-8778-5a8c3669cda5',
    intlLabels.procedure.usedCode,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.usedCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'b0252e8d-2ceb-4998-ad03-286ac03f9ce4',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const encounterPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    'dcb54324-e788-4028-86dd-8b0937133944',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '16b87ed2-fe38-4759-a24b-7d98d6e064ff',
    intlLabels.shared.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    'bdba7cb0-fb34-4dd9-a21d-77c72f50bd7f',
    intlLabels.encounter.class,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.class', Coding),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'ba15cc2b-c3eb-4242-a7ec-1d1c695f12bb',
    intlLabels.shared.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.type', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '2043c047-3146-47bb-8891-60f2a803c71f',
    intlLabels.shared.priority,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.priority', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '5ef4e706-4024-49d6-b909-2b93688048b5',
    intlLabels.shared.time,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.period',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    '0375a24e-5273-46bd-87cf-da09310ca0d4',
    intlLabels.encounter.length,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.length', Quantity),
      ]),
  ),
  new PropertyConfig(
    '6c6c4cfc-7cdd-486e-b009-1750c7884222',
    intlLabels.encounter.reason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reason', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    '56d96a48-2136-4dd8-b576-a9ad10ded28a',
    intlLabels.encounter.hospitalizationAdmitSource,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.hospitalization.admitSource',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    'de26fa27-abcd-445e-afa2-94ad6a109d02',
    intlLabels.encounter.hospitalizationReAdmission,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.hospitalization.reAdmission',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '4e301350-e1b2-4eee-88a3-f55391f56a75',
    intlLabels.encounter.hospitalizationDietPreferences,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.hospitalization.dietPreference',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirArray,
          {
            fhirArray: {
              arrayElementParser: new CodeableConcept(),
              concatenate: true,
            },
          },
        ),
      ]),
  ),
  new PropertyConfig(
    'f65624f6-e752-4b2e-b131-eaefe8544cb3',
    intlLabels.encounter.hospitalizationSpecialCourtesy,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.hospitalization.specialCourtesy',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirArray,
          {
            fhirArray: {
              arrayElementParser: new CodeableConcept(),
              concatenate: true,
            },
          },
        ),
      ]),
  ),
  new PropertyConfig(
    '6a92ecc0-6ea1-419b-9085-ffbbf5ad22c5',
    intlLabels.encounter.hospitalizationSpecialArrangement,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.hospitalization.specialArrangement',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirArray,
          {
            fhirArray: {
              arrayElementParser: new CodeableConcept(),
              concatenate: true,
            },
          },
        ),
      ]),
  ),
  new PropertyConfig(
    '9be5e2de-b5d8-43bb-b164-9dc99f9bdab0',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const conditionPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    'dec44f5e-7f81-4039-aa34-adac7d9cb900',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '27db99c1-325e-4869-b7da-b42c8b7693a8',
    intlLabels.condition.clinicalStatus,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.clinicalStatus', Code),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '0ebfbc58-215f-4046-9a86-df4c0ce8c6cf',
    intlLabels.condition.verificationStatus,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.verificationStatus', Code),
      ]),
  ),
  new PropertyConfig(
    '3e00270d-7238-4ecc-89bc-10c0eb24aa4f',
    intlLabels.shared.category,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.category', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '0d7f0629-a67b-4559-b111-5646bbc264a5',
    intlLabels.shared.severity,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.severity', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '7a6094af-5d47-42de-a955-efc07b434c56',
    intlLabels.shared.code,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.code', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '27c91fea-4e7d-4da9-a574-b8dd7c0cc896',
    intlLabels.shared.bodySite,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.bodySite', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '1e457cc9-20be-43a7-980d-db8fb798753f',
    intlLabels.condition.onset,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.onsetDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.onsetAge', Quantity),
        new FhirPropertyParser(
          'resource.onsetPeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.onsetRange', Range),
        new FhirPropertyParser('resource.onsetString', FhirString),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    '07d2c0ff-6f53-4cbf-874f-9d13eefbdb73',
    intlLabels.condition.abatement,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.abatementDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.abatementAge', Quantity),
        new FhirPropertyParser('resource.abatementBoolean', FhirBoolean),
        new FhirPropertyParser(
          'resource.abatementPeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.abatementRange', Range),
        new FhirPropertyParser('resource.abatementString', FhirString),
      ]),
  ),
  new PropertyConfig(
    'db7d889d-39a9-466f-88a4-b08df03aaaaa',
    intlLabels.condition.asserted,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.assertedDate',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '912a0bdd-9230-4eb5-ae7c-c3edc0d4074f',
    intlLabels.shared.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '5cbc4d66-695c-452d-8579-8a2f171035fb',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const medicationRequestPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    'b3efa335-61f2-4db6-a32c-5136e3ccc4af',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '1b8ce76c-2641-4ace-9fb2-a92b0935485e',
    intlLabels.medicationRequest.groupIdentifier,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.groupIdentifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '42f96345-71c5-4828-aadc-4e3db2db683d',
    intlLabels.shared.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 5 },
  ),
  new PropertyConfig(
    '7a8c9bbb-f52f-4ba8-a360-e8d7a7ef3a53',
    intlLabels.medicationRequest.intent,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.intent', Code),
      ]),
  ),
  new PropertyConfig(
    '0d59152e-8b5f-4bc4-893c-d28b73c13c96',
    intlLabels.shared.category,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.category', CodeableConcept),
      ]),
    { priority: 4 },
  ),
  new PropertyConfig(
    'a0fd7275-356c-4ed2-8bb9-2214076b26b3',
    intlLabels.shared.priority,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.priority', Code),
      ]),
  ),
  new PropertyConfig(
    'a28214d5-a0b8-418b-962c-a0ba2d5595bb',
    intlLabels.shared.medication,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.medicationCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.contained', FhirArray, {
          fhirArray: {
            optionalPathParsers: [
              {
                arrayElementPath: 'code',
                arrayElementParser: new CodeableConcept(),
              },
            ],
            concatenate: true,
          },
        }),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'f42c3617-afaa-43d8-a612-ffa061ddbe66',
    intlLabels.medicationRequest.authoredOn,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.authoredOn',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    'a91bbcb9-580b-4ea9-a571-01734570b27b',
    intlLabels.shared.reason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '34151e35-6a48-46a6-b0d3-3207e812efc1',
    intlLabels.shared.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '942b37ca-4162-45cb-805e-cac994d809c9',
    intlLabels.shared.dosageText,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].text',
          FhirString,
        ),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'c8a98bba-7992-44df-b73f-deb00e1e0a1a',
    intlLabels.shared.dosageAdditionalInstruction,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].additionalInstruction',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirArray,
          {
            fhirArray: {
              arrayElementParser: new CodeableConcept(),
              concatenate: true,
            },
          },
        ),
      ]),
  ),
  new PropertyConfig(
    'ef7b9ba9-07b0-417f-9f0a-fb2e7e025f19',
    intlLabels.shared.dosagePatientInstruction,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].patientInstruction',
          FhirString,
        ),
      ]),
  ),
  new PropertyConfig(
    'd49353cc-2633-4be4-b85a-859f88bf4eed',
    intlLabels.shared.dosageTiming,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosageInstruction[0].timing', Timing),
      ]),
  ),
  new PropertyConfig(
    '6516a12b-7a85-4eb1-86dd-9f57d76cc34d',
    intlLabels.shared.dosageAsNeeded,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].asNeededBoolean',
          FhirBoolean,
        ),
        new FhirPropertyParser(
          'resource.dosageInstruction[0].asNeededCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '0ba87f19-d50e-4e27-891f-3393d7876d1b',
    intlLabels.shared.dosageSite,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].site',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '9ab4ffd3-9172-4a51-8c2a-ed3bf1229295',
    intlLabels.shared.dosageRoute,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].route',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    'fc3802d1-bbb1-464b-a0f8-09fb5351c37f',
    intlLabels.shared.dosageMethod,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].method',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '9cb9a0c5-d01d-49f4-889e-96fe52275c05',
    intlLabels.shared.dosageDose,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].doseRange',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Range,
        ),
        new FhirPropertyParser(
          'resource.dosageInstruction[0].doseQuantity',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Quantity,
        ),
      ]),
  ),
  new PropertyConfig(
    '5027336e-2c47-4b9c-8db4-7e13ae4130b3',
    intlLabels.shared.dosageRate,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].rateRatio',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Ratio,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosageInstruction[0].rateRage', Range),
        new FhirPropertyParser(
          'resource.dosageInstruction[0].rateQuantity',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Quantity,
        ),
      ]),
  ),
  new PropertyConfig(
    'da3b9a0c-9d6f-409c-a48a-0dddf33755cb',
    intlLabels.medicationRequest.dispenceValidityPeriod,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dispenseRequest.validityPeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '827cb0e9-b15e-4bb9-8088-18c41200b304',
    intlLabels.medicationRequest.dispenseNumberOfRepeatsAllowed,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dispenseRequest.numberOfRepeatsAllowed',
          Integer,
        ),
      ]),
  ),
  new PropertyConfig(
    'cebfff86-913d-404d-ab0c-d75776a219b3',
    intlLabels.medicationRequest.dispenseQuanity,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dispenseRequest.quantity', Quantity),
      ]),
  ),
  new PropertyConfig(
    '95c43284-c926-46e1-a30c-83262be2808d',
    intlLabels.medicationRequest.dispenseSupplyDuration,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dispenseRequest.expectedSupplyDuration',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Quantity,
        ),
      ]),
  ),
  new PropertyConfig(
    '1014f5eb-d8f0-48ca-be25-b9037b5b7967',
    intlLabels.medicationRequest.subsitutionAllowed,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.substitution.allowed', FhirBoolean),
      ]),
  ),
  new PropertyConfig(
    '9a45fe65-acb4-45d8-96c3-50e31b743ed4',
    intlLabels.medicationRequest.substitutionReason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.substitution.reason', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'd068677e-88a9-492f-a31a-312b49105c4b',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const medicationAdministrationPropertyConfigs = (
  parseConfig: ParseConfiguration,
) => [
  new PropertyConfig(
    'abbbb82d-1c92-4aaa-a4d8-23f13ba4251e',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    'ec36fb76-93a7-4d09-8731-1891748e6759',
    intlLabels.shared.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '0b914c8c-a3de-40a9-831e-f2482f243bd5',
    intlLabels.shared.category,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.category', CodeableConcept),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    'd697cba9-6edc-4067-89f2-db4e2cb8f535',
    intlLabels.shared.medication,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.medicationCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.contained', FhirArray, {
          fhirArray: {
            optionalPathParsers: [
              {
                arrayElementPath: 'code',
                arrayElementParser: new CodeableConcept(),
              },
            ],
            concatenate: true,
          },
        }),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '6d68506f-7613-4b3a-8296-2bc61fb9fae7',
    intlLabels.shared.time,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.effectiveDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.effectivePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    'cd3bb315-b77c-4398-b5f6-2885e943cb5e',
    intlLabels.medicationAdministration.notGiven,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.notGiven', FhirBoolean),
      ]),
  ),
  new PropertyConfig(
    'fe2678f6-c02a-4bb1-bf4e-3c5aee129d10',
    intlLabels.medicationAdministration.reasonNotGiven,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonNotGiven', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'c9907c82-db89-4b6d-b2b5-04ad76a1c348',
    intlLabels.medicationAdministration.reasonCode,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '355f43df-a7d2-4262-a44e-3cb109aaca5b',
    intlLabels.shared.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'ebfcdf84-d841-4cb3-9efd-2b1e6a882308',
    intlLabels.shared.dosageText,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.dosage.text', FhirString),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'e9799e3d-af1b-481d-8c5f-25be51a8040d',
    intlLabels.shared.dosageSite,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage.site', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'f9d83f5e-29b8-459b-a13f-bb541fb1d93c',
    intlLabels.shared.dosageRoute,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage.route', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'd20239cc-f1e6-47d2-847e-2e7498aa7b8c',
    intlLabels.shared.dosageMethod,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage.method', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '8a6183d7-efd8-4c2b-b052-7d1d1df43370',
    intlLabels.shared.dosageDose,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage.dose', Quantity),
      ]),
  ),
  new PropertyConfig(
    'a99dd81c-d9e0-4790-8b84-d709477eabcb',
    intlLabels.shared.dosageRate,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage.rateRatio', Ratio),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage.rateQuantity', Quantity),
      ]),
  ),
  new PropertyConfig(
    'b36a1d21-5fbb-4207-95a4-87a6e1747636',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const medicationStatementPropertyConfigs = (
  parseConfig: ParseConfiguration,
) => [
  new PropertyConfig(
    'e298e0fa-74af-447b-ad9f-ffc88c7a56e2',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '1f83b007-309a-4e38-b53a-cdd0a7e1b502',
    intlLabels.shared.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '4a1082c1-327d-4bc2-9b4a-a379e9b3a2c8',
    intlLabels.shared.category,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.category', CodeableConcept),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    '9a98c452-f089-4b03-a216-f37d5f41a168',
    intlLabels.shared.medication,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.medicationCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.contained', FhirArray, {
          fhirArray: {
            optionalPathParsers: [
              {
                arrayElementPath: 'code',
                arrayElementParser: new CodeableConcept(),
              },
            ],
            concatenate: true,
          },
        }),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'b7261cdc-1e6a-4cdd-b37d-94153349aa70',
    intlLabels.shared.time,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.effectiveDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.effectivePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    '0216126f-67e5-4b84-8e55-92ce737282a2',
    intlLabels.medicationStatement.dateAsserted,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dateAsserted',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '5b5543f0-667d-4b98-8888-257b82e6f3a0',
    intlLabels.medicationStatement.taken,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.taken', Code),
      ]),
  ),
  new PropertyConfig(
    'fa4954c2-7a37-4468-8483-59eaebade596',
    intlLabels.medicationStatement.reasonNotTaken,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonNotTaken', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '0a5aa708-8651-4761-a57f-18c35aaad936',
    intlLabels.medicationStatement.reasonTaken,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '3bd57681-8708-47ad-8203-d7e3ed54f4c1',
    intlLabels.shared.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'bad09cb0-3d35-4585-be7d-ff670a159886',
    intlLabels.shared.dosageText,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.dosage[0].text', FhirString),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '3cc8e02e-3638-46ea-bba4-1c6fae1aeeff',
    intlLabels.shared.dosageAdditionalInstruction,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosage[0].additionalInstruction',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirArray,
          {
            fhirArray: {
              arrayElementParser: new CodeableConcept(),
              concatenate: true,
            },
          },
        ),
      ]),
  ),
  new PropertyConfig(
    '8be7a754-9f23-4d6e-9ad1-62ef3f8fecec',
    intlLabels.shared.dosagePatientInstruction,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosage[0].patientInstruction',
          FhirString,
        ),
      ]),
  ),
  new PropertyConfig(
    '49a26b5e-5a0f-4e6f-b150-c43aef3d46a6',
    intlLabels.shared.dosageTiming,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].timing', Timing),
      ]),
  ),
  new PropertyConfig(
    '487aa166-4287-4349-88dd-73aaf830937d',
    intlLabels.shared.dosageAsNeeded,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosage[0].asNeededBoolean',
          FhirBoolean,
        ),
        new FhirPropertyParser(
          'resource.dosage[0].asNeededCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    'f645fb31-c011-4b7e-9c2e-1d0a95310f44',
    intlLabels.shared.dosageSite,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].site', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '3afae998-7f49-4dd3-b21d-b65d7e6b339a',
    intlLabels.shared.dosageRoute,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].route', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '49ffea91-2a5d-4599-a4fd-034f006b215b',
    intlLabels.shared.dosageMethod,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].method', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'bc13b482-2293-4f9d-967b-00500cf1534f',
    intlLabels.shared.dosageDose,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].doseRange', Range),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].doseQuantity', Quantity),
      ]),
  ),
  new PropertyConfig(
    '3a53b879-f2ea-4f87-a0a1-4f01073dc28b',
    intlLabels.shared.dosageRate,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].rateRatio', Ratio),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].rateRange', Range),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosage[0].rateQuantity', Quantity),
      ]),
  ),
  new PropertyConfig(
    'a10be93d-046f-4a42-a597-5e8b0a34e998',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const mediaPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '6c3bd9cf-b22f-4201-95d7-da52f2e87e22',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '8c1a36b3-fcc9-4909-825d-c4bd6778ce8d',
    intlLabels.media.type,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.type', Code),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    'd62ad718-c5b4-4c92-a5b9-e72d0bcb87ef',
    intlLabels.media.subType,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.subtype', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '12afba82-b673-4c2c-a1f1-65287e1ce00d',
    intlLabels.media.view,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.view', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'b68daafb-3571-4c43-b76c-6e4ffa50c5e9',
    intlLabels.media.time,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.occurrenceDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.occurrencePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    '39be2936-51c8-4bc9-bed6-dcc562bbd06d',
    intlLabels.media.reason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    '2abc5668-bfcd-47aa-a537-cad09cd5886e',
    intlLabels.media.bodySite,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.bodySite', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'ca974d44-350a-4726-976a-fbfa780d385b',
    intlLabels.media.height,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.height', Integer),
      ]),
  ),
  new PropertyConfig(
    '447e13cf-b3c1-4e47-a5a4-a4723981a83c',
    intlLabels.media.width,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.width', Integer),
      ]),
  ),
  new PropertyConfig(
    'ad1a4812-607f-4f05-93fc-a584cb52d596',
    intlLabels.media.frames,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.frames', Integer),
      ]),
  ),
  new PropertyConfig(
    '3c485710-0cae-4bc4-9207-ed3d2dbeff11',
    intlLabels.media.duration,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.duration', Integer),
      ]),
  ),
  new PropertyConfig(
    'cf9a4cf2-f352-49bb-ab0d-30c8b2b0f9ed',
    intlLabels.media.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'd8746dd8-85b0-4cb3-8615-37929b714336',
    intlLabels.shared.link,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.content', null, {}),
      ]),
    { priority: 1, isLink: true },
  ),
  new PropertyConfig(
    '5eb4c9e0-d657-4f15-9255-b71618daa9f8',
    intlLabels.media.contentType,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.content.contentType', Code),
      ]),
  ),
  new PropertyConfig(
    '0befe971-ba22-498e-a7f7-75830bc9da01',
    intlLabels.media.title,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.content.title', FhirString),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '943fc002-3c8d-4175-ab0d-b75dea59e557',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const documentReferencePropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '8f5a392c-24d9-4338-b864-915df50e3408',
    intlLabels.documentReference.masterIdentifier,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.masterIdentifier', FhirIdentifier),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '53e7fcbe-be12-437c-8db6-0b250034070b',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '82acab0d-20b9-41d5-81b4-9efba9f023ef',
    intlLabels.documentReference.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
  ),
  new PropertyConfig(
    '295e7b54-49c9-4804-ac75-6c6ca7c9aa4c',
    intlLabels.documentReference.docStatus,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.docStatus', Code),
      ]),
  ),
  new PropertyConfig(
    '7a9ade96-ef62-44b8-8def-5d25c8b98832',
    intlLabels.documentReference.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.type', CodeableConcept),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    '969e3508-6e64-4647-8997-c46b55b09b75',
    intlLabels.documentReference.class,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.class', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '6fb08933-f452-4923-83de-603b3f66478a',
    intlLabels.documentReference.createTime,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.created',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    'f7b4799f-78e5-498a-ad2c-4d512300f710',
    intlLabels.documentReference.time,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.indexed',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    'f323247a-d675-4fec-bbaf-612d9f1e6e27',
    intlLabels.documentReference.description,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.description', FhirString),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '21dc1f7c-add8-4f36-aecc-db0900f98532',
    intlLabels.documentReference.securityLabel,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.securityLabel', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'fde4b1e4-a6b2-47dd-b414-c11b9161e4c7',
    intlLabels.shared.link,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.content[0].attachment', null),
      ]),
    { isLink: true },
  ),
  new PropertyConfig(
    'b55b96c0-b7e9-4fb9-87ce-5cfbf72641ef',
    intlLabels.documentReference.event,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.context.event', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    'ad531eff-2c0d-4ae4-adb4-23bad6e70ac2',
    intlLabels.documentReference.period,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.context.period',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    'f53f204b-c1a7-474f-98ee-b0a9912a4036',
    intlLabels.documentReference.facilityType,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.context.facilityType',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '08139cf0-13b8-4f9a-b3ad-ebf2f5dda9f6',
    intlLabels.documentReference.practiceSetting,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.context.practiceSetting',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '21978131-fe4a-4f68-80ac-9ea0deaae3ff',
    intlLabels.documentReference.contentType,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.content[0].attachment.contentType',
          Code,
        ),
      ]),
  ),
  new PropertyConfig(
    '15327f57-14a2-4694-8425-5754b87d2a3c',
    intlLabels.documentReference.title,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.content[0].attachment.title',
          FhirString,
        ),
      ]),
  ),
  new PropertyConfig(
    'da3cb785-eada-4a3b-bee8-e047dd927a5f',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const diagnosticReportPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '3eaafbb5-3f99-413a-9c5e-7f59bd248d57',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '6a9d2bc7-9b54-42d2-93b6-8130e142a4db',
    intlLabels.diagnosticReport.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    '6facf34f-5a1f-47d6-af39-9cdfe19b3bed',
    intlLabels.diagnosticReport.category,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.category', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'd1113f82-448f-4386-ae0a-3458d25c7da9',
    intlLabels.diagnosticReport.code,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.code', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '9c9e1dbb-2ae7-40ea-9f25-ce48c6ab833e',
    intlLabels.diagnosticReport.codedDiagnosis,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.codedDiagnosis', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '9045f878-c0c6-4080-8cb8-484b09c1c0a5',
    intlLabels.diagnosticReport.conclusion,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.conclusion', FhirString),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '7db232e2-7052-47d9-951b-aca778580cd7',
    intlLabels.diagnosticReport.title,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.presentedForm[0].title', FhirString),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'bc6b3118-89e8-4960-9370-e63621eea740',
    intlLabels.diagnosticReport.issued,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.issued',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    'c638eccc-4610-4701-b96a-0022a6beb10c',
    intlLabels.shared.link,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.presentedForm[0]', null),
      ]),
    { isLink: true },
  ),
  new PropertyConfig(
    'bbbbaf85-0f3d-4d80-a191-637d628d2f08',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const medicationDispensePropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    'cddab12f-839f-4fe3-bcef-be69f39c6267',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    'ea8b8d04-abd3-4a1f-a396-112dd9989110',
    intlLabels.medicationDispense.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
  ),
  new PropertyConfig(
    '4b166456-c985-4b74-9a02-fbe790031c37',
    intlLabels.medicationDispense.medication,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.medicationCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'e4d9fe81-e96a-40f1-9c1f-f27f52a1b3d5',
    intlLabels.medicationDispense.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.type', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '39533d87-b660-45c8-86fb-4528c12efeed',
    intlLabels.medicationDispense.quantity,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.quantity', Quantity),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'f59bb62a-b042-460e-9431-a718e4a93f7b',
    intlLabels.medicationDispense.daysSupply,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.daysSupply', Quantity),
      ]),
  ),
  new PropertyConfig(
    'f68587cf-4cc1-4ff3-b522-48c8670a62eb',
    intlLabels.medicationDispense.whenPrepared,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.whenPrepared',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '8a0e5af7-69b0-4a37-aaca-c2e3fee74614',
    intlLabels.medicationDispense.whenHandedOver,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.whenHandedOver',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '25ec1689-7040-470e-83f0-e895a937c76d',
    intlLabels.medicationDispense.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'c73d6634-2643-442f-9c17-97ec455b89d9',
    intlLabels.shared.dosageText,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].text',
          FhirString,
        ),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    'f5e7629a-5e82-4d52-99a9-a79892b6f8f0',
    intlLabels.shared.dosageAdditionalInstruction,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].additionalInstruction',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirArray,
          {
            fhirArray: {
              arrayElementParser: new CodeableConcept(),
              concatenate: true,
            },
          },
        ),
      ]),
  ),
  new PropertyConfig(
    '9913e5f6-e1ff-4d29-b966-acb5d1f916bc',
    intlLabels.shared.dosagePatientInstruction,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].patientInstruction',
          FhirString,
        ),
      ]),
  ),
  new PropertyConfig(
    '3cd8101d-299c-426b-802a-80bf9f39fe91',
    intlLabels.shared.dosageTiming,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosageInstruction[0].timing', Timing),
      ]),
  ),
  new PropertyConfig(
    'd8343dcc-418a-45dd-98e8-ee2a4b2657a9',
    intlLabels.shared.dosageAsNeeded,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].asNeededBoolean',
          FhirBoolean,
        ),
        new FhirPropertyParser(
          'resource.dosageInstruction[0].asNeededCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '65f3f93f-26ee-4178-b46e-9b65295a7ce2',
    intlLabels.shared.dosageSite,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].site',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '875ce9fc-0835-480a-8526-adb224491c31',
    intlLabels.shared.dosageRoute,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].route',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '09bc0ff3-b26d-44d2-bd64-c5af8e6c9ae1',
    intlLabels.shared.dosageMethod,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].method',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    '838040c7-d874-45f7-8ac8-a396c5178d89',
    intlLabels.shared.dosageDose,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].doseRange',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Range,
        ),
        new FhirPropertyParser(
          'resource.dosageInstruction[0].doseQuantity',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Quantity,
        ),
      ]),
  ),
  new PropertyConfig(
    '63923c29-bcd8-426d-8dd2-acefb1869cb0',
    intlLabels.shared.dosageRate,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dosageInstruction[0].rateRatio',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Ratio,
        ),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.dosageInstruction[0].rateRage', Range),
        new FhirPropertyParser(
          'resource.dosageInstruction[0].rateQuantity',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Quantity,
        ),
      ]),
  ),
  new PropertyConfig(
    '9d1d930b-d20d-4f80-a313-4038de8c4020',
    intlLabels.medicationDispense.wasSubstituted,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.substitution.wasSubstituted',
          FhirBoolean,
        ),
      ]),
  ),
  new PropertyConfig(
    'bd3761f8-3c31-4f0d-bf8b-e3545a5fdc53',
    intlLabels.medicationDispense.substitutionType,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.substitution.type', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '70f04410-1f7e-4365-a858-3413d017761e',
    intlLabels.medicationDispense.substitutionReason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.substitution.reason', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'a0578857-50f5-42af-9a27-0a5627953219',
    intlLabels.medicationDispense.notDone,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.notDone', FhirBoolean),
      ]),
  ),
  new PropertyConfig(
    '454dd192-81d0-4179-af8a-ab2e6170a401',
    intlLabels.medicationDispense.notDoneReason,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.notDoneReasonCodeableConcept',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    'df8b364a-068b-4305-9f09-07b8ad307bf2',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const referralRequestPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '6d6d54c3-69ba-4c11-b0ce-533b2489811c',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '7039eed7-577f-4f70-b0db-34016e9d3700',
    intlLabels.referralRequest.groupIdentifier,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.groupIdentifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '56f4ddc3-6b1d-4754-bd29-0d1992630f19',
    intlLabels.referralRequest.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
  ),
  new PropertyConfig(
    'a58124d1-68cc-455e-9aca-763c27ba657c',
    intlLabels.referralRequest.intent,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.intent', Code),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    '3742065c-dbc6-4321-b75a-82cc60a7c7c2',
    intlLabels.referralRequest.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.type', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'f73c8fbc-6362-4c64-8e69-92679c600e9b',
    intlLabels.referralRequest.priority,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.priority', Code),
      ]),
  ),
  new PropertyConfig(
    '3e7ecd63-7155-48d1-9f2b-d4d9ae5d2fb8',
    intlLabels.referralRequest.serviceRequested,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.serviceRequested', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
          },
        }),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'f953ac4c-3dcf-4e3f-b4bd-fb2b14a53d32',
    intlLabels.referralRequest.whenServiceRequested,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.occurrenceDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.occurrencePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '22384fd9-9a7a-4842-8b5b-c3cfaf77748d',
    intlLabels.referralRequest.authoredOn,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.authoredOn',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
    { defaultSort: true },
  ),
  new PropertyConfig(
    '393d4e48-70a6-45a5-b954-1f9436e2e86a',
    intlLabels.referralRequest.specialty,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.specialty', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    'ec806cbe-4929-4980-bf63-d1ff6bc061d4',
    intlLabels.referralRequest.reason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
            concatenate: true,
          },
        }),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '9220b96d-e1d3-4d7d-844a-816ab27283d7',
    intlLabels.referralRequest.description,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.description', FhirString),
      ]),
  ),
  new PropertyConfig(
    '2cf4f4ee-3dfa-4e24-8115-23594ad509b2',
    intlLabels.referralRequest.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
            concatenate: true,
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'c5358386-da48-42f0-8a8e-1a05ba3b2d1c',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const specimenPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '6009ea65-734f-443f-b26a-d5fbf4d03f51',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '09f1d378-b1fd-4aa7-8fa4-e1842d9c6882',
    intlLabels.specimen.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.type', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '7c3cc7e7-a95e-4021-9ab1-40e5d85c6a97',
    intlLabels.specimen.accessionIdentifier,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.accessionIdentifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    'fa9698a8-a164-4424-9833-3c22b71c7030',
    intlLabels.specimen.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '1f690923-4c07-4ec1-a45b-940b8ac1e450',
    intlLabels.specimen.receivedTime,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.receivedTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '050d25e1-3d2b-4672-b33b-7fbd61e3a4fe',
    intlLabels.specimen.collection,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.collection.collectedDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.collection.collectedPeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '16fe6391-bad9-42a3-8776-dab9fb7a17da',
    intlLabels.specimen.collectionQuantity,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.collection.quantity', Quantity),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '28615cd6-b59e-4ff8-95bc-20d73bc10383',
    intlLabels.specimen.collectionMethod,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.collection.method', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '506cba54-0e62-4812-82bb-f76f9d69765b',
    intlLabels.specimen.collectionBodySite,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.collection.bodySite', CodeableConcept),
      ]),
  ),
  new PropertyConfig(
    '193c0ffc-cfb7-4178-b931-7138f16995bc',
    intlLabels.specimen.description,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.processing[0].description',
          FhirString,
        ),
      ]),
    { priority: 3 },
  ),
  new PropertyConfig(
    'e0f7a10a-a191-43db-9ac4-b5b93c041648',
    intlLabels.specimen.procedure,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.processing[0].procedure',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          CodeableConcept,
        ),
      ]),
  ),
  new PropertyConfig(
    'f20f28f4-54f3-4440-8a30-ef3197db01e3',
    intlLabels.specimen.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
          },
        }),
      ]),
    { priority: 4 },
  ),
  new PropertyConfig(
    '1e69b1dc-0537-46da-8d97-bf17d3b95590',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const imagingStudyPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '2a14c421-c00e-48da-ae1a-ac340cd8d2dd',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '8640c313-877e-451e-ad41-b56ff2cf1b9e',
    intlLabels.imagingStudy.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '016dfc85-ded9-4ad7-917c-25b89c4f882c',
    intlLabels.imagingStudy.description,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.description', FhirString),
      ]),
  ),
  new PropertyConfig(
    '7b5a342e-b813-4d23-9cfc-9e4a77bfd65a',
    intlLabels.imagingStudy.modality,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.modality', FhirArray, {
          fhirArray: {
            arrayElementParser: new Coding(),
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'b9641228-2e8c-45ae-ac1e-5cb2f258ba9e',
    intlLabels.imagingStudy.started,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.started',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '9373434d-82cf-4ab7-8ae3-b73f1fe2c76e',
    intlLabels.imagingStudy.procedure,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.procedureCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
          },
        }),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '4a2bc025-9ca1-4a56-a259-1d07fcc17bb7',
    intlLabels.imagingStudy.reason,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.reasonCode', FhirArray, {
          fhirArray: {
            arrayElementParser: new CodeableConcept(),
          },
        }),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'd1631baf-ccfd-4ab2-bcca-3fe67a2d1561',
    intlLabels.imagingStudy.note,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.note', FhirArray, {
          fhirArray: {
            arrayElementParser: new Annotation(),
          },
        }),
      ]),
  ),
  new PropertyConfig(
    'db86f71e-9462-45bf-9652-7448a1752f38',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const consentPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    'f55ec023-a05c-4e90-8468-a50e2594aec4',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    'f75cd936-9a64-412f-b798-c2dff8a4ae40',
    intlLabels.consent.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', Code),
      ]),
  ),
  new PropertyConfig(
    'd8e0e866-dcfc-402f-8be5-1f395c1c02a8',
    intlLabels.consent.date,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.dateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '8876585a-a725-47cf-8ac3-2224974859f1',
    intlLabels.consent.sourceReference,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.sourceReference.display', FhirString),
      ]),
  ),
  new PropertyConfig(
    'dfdc1d7d-82b2-40ec-a499-a82f87708baf',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const procedureRequestPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '8001bd5c-38fb-4bfb-8b93-e8be9cc91466',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    'c3b1d897-ffe0-4d07-9435-413b953d9445',
    intlLabels.shared.code,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.code', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '774c6fad-6b5c-43ef-a3eb-0e4d19b7b3fa',
    intlLabels.procedureRequest.authoredOn,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.authoredOn',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '0ea6449d-24e5-43c9-8f90-7b88dc6441c1',
    intlLabels.procedureRequest.occurrence,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.occurrenceDateTime',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.occurrencePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.dateTimeOptions,
        ),
        new FhirPropertyParser(
          'resource.occurrenceTiming',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Timing,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '298cac3c-55f1-4ebc-b58e-543eee4f4983',
    intlLabels.procedureRequest.intent,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.intent',
          Code,
          parseConfig.dateTimeOptions,
        ),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'a5df62e5-734c-4eec-a1cc-caf568b00e79',
    intlLabels.procedureRequest.priority,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.priority',
          Code,
          parseConfig.dateTimeOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    '597b0f0e-5deb-4855-88ac-eac81de8676f',
    intlLabels.procedureRequest.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.status',
          Code,
          parseConfig.dateTimeOptions,
        ),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    'e05deec4-e23c-49d2-84a2-ce4a56231118',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const coveragePropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    'aad7881c-df08-4a4b-a48a-668d18e3e996',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    'eecf662e-3e6b-4da1-b5e4-0efdf1b301c2',
    intlLabels.shared.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.type', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'f73f5c84-d3c6-4e62-8307-b621d6a41ca6',
    intlLabels.coverage.period,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.period',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    '3df8fa09-860a-4b8f-89f8-1e64460879df',
    intlLabels.coverage.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', FhirString),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    '88a358de-4b3b-4e6c-9aad-d06e48ca9a01',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const eobPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '000901dd-faab-4099-9f9b-a6d3bf5ea57e',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.identifier', FhirIdentifier),
      ]),
  ),
  new PropertyConfig(
    '2a6c33fa-cfb5-4d1c-8955-8b0bf84e9917',
    intlLabels.shared.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.type', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    'a869d6f2-cdbf-41f2-96d0-f3f1d126e043',
    intlLabels.explanationOfBenefit.billablePeriod,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.billablePeriod',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          Period,
          parseConfig.periodOptions,
        ),
      ]),
  ),
  new PropertyConfig(
    'c7a8ee9f-30b8-4565-9d00-5e9a46dbb0fe',
    intlLabels.explanationOfBenefit.status,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.status', FhirString),
      ]),
  ),
  new PropertyConfig(
    '52688263-b798-46cc-a014-b3e4ab421350',
    intlLabels.explanationOfBenefit.outcome,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.outcome', CodeableConcept),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'ff408238-7202-4531-a45a-a29e7173cd93',
    intlLabels.explanationOfBenefit.totalCost,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.totalCost', Amount),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    'df9ecc5a-d3be-4e79-95c6-4805e8176ed2',
    intlLabels.explanationOfBenefit.payment,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.payment.amount', Amount),
      ]),
  ),
];

const immunizationPropertyConfigs = (parseConfig: ParseConfiguration) => [
  new PropertyConfig(
    '5f00aea6-e989-4aa2-ab9d-8ed5192c7895',
    intlLabels.shared.id,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.id', FhirString),
      ]),
  ),
  new PropertyConfig(
    'c811fab9-9a28-4e1e-8bc3-1fa36c624152',
    intlLabels.shared.type,
    () =>
      new FhirPropertyParserGroup([
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        new FhirPropertyParser('resource.vaccineCode', CodeableConcept),
      ]),
    { priority: 0 },
  ),
  new PropertyConfig(
    '6b6eaf3b-b3b0-4879-92db-33f11153d7d9',
    intlLabels.immunization.date,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser(
          'resource.date',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          FhirDateTime,
          parseConfig.dateTimeOptions,
        ),
      ]),
    { priority: 2 },
  ),
  new PropertyConfig(
    'e037cad3-66f7-46b6-aa22-3a2c545e8495',
    intlLabels.immunization.manufacturer,
    () =>
      new FhirPropertyParserGroup([
        new FhirPropertyParser('resource.manufacturer.display', FhirString),
      ]),
    { priority: 1 },
  ),
  new PropertyConfig(
    'b6032e33-ce40-4b44-8f30-e1c98df32292',
    intlLabels.shared.favorite,
    () => new FhirPropertyParserGroup([]),
    { isFavorite: true },
  ),
];

const labels = {
  titles: {
    observation: {
      id: 'pages.patientViewer.table.selections.title.observation',
      defaultMessage: 'Observation',
    },
    procedure: {
      id: 'pages.patientViewer.table.selections.title.procedure',
      defaultMessage: 'Procedure',
    },
    encounter: {
      id: 'pages.patientViewer.table.selections.title.encounter',
      defaultMessage: 'Encounter',
    },
    conditions: {
      id: 'pages.patientViewer.table.selections.title.condition',
      defaultMessage: 'Condition',
    },
    medicationRequest: {
      id: 'pages.patientViewer.table.selections.title.medicationRequest',
      defaultMessage: 'Medication Request',
    },
    medicationAdministration: {
      id: 'pages.patientViewer.table.selections.title.medicationAdministration',
      defaultMessage: 'Medication Administration',
    },
    medicationStatement: {
      id: 'pages.patientViewer.table.selections.title.medicationStatement',
      defaultMessage: 'Medication Statement',
    },
    media: {
      id: 'pages.patientViewer.table.selections.title.media',
      defaultMessage: 'Media',
    },
    documentReference: {
      id: 'pages.patientViewer.table.selections.title.documentReferences',
      defaultMessage: 'Document Reference',
    },
    diagnosticReport: {
      id: 'pages.patientViewer.table.selections.title.diagnosticReport',
      defaultMessage: 'Diagnostic Report',
    },
    medicationDispense: {
      id: 'pages.patientViewer.table.selections.title.medicationDispense',
      defaultMessage: 'Medication Dispense',
    },
    referralRequest: {
      id: 'pages.patientViewer.table.selections.title.referralRequest',
      defaultMessage: 'Referral Request',
    },
    specimen: {
      id: 'pages.patientViewer.table.selections.title.specimen',
      defaultMessage: 'Specimen',
    },
    imagingStudy: {
      id: 'pages.patientViewer.table.selections.title.imagingStudy',
      defaultMessage: 'Imaging Study',
    },
    consent: {
      id: 'pages.patientViewer.table.selections.title.consent',
      defaultMessage: 'Consent',
    },
    procedureRequest: {
      id: 'pages.patientViewer.table.selections.title.procedureRequest',
      defaultMessage: 'Procedure Request',
    },
    coverage: {
      id: 'pages.patientViewer.table.selections.title.coverage',
      defaultMessage: 'Coverage',
    },
    explanationOfBenefit: {
      id: 'pages.patientViewer.table.selections.title.explanationOfBenefit',
      defaultMessage: 'ExplanationOfBenefit',
    },
    immunization: {
      id: 'pages.patientViewer.table.selections.title.immunization',
      defaultMessage: 'Immunization',
    },
  },
};

export const resourceConfigs = [
  {
    title: labels.titles.observation,
    dataSource: dataSourceTypes.observations.id,
    propertyConfigs: observationPropertyConfigs,
  },
  {
    title: labels.titles.procedure,
    dataSource: dataSourceTypes.procedures.id,
    propertyConfigs: procedurePropertyConfigs,
  },
  {
    title: labels.titles.encounter,
    dataSource: dataSourceTypes.encounters.id,
    propertyConfigs: encounterPropertyConfigs,
  },
  {
    title: labels.titles.conditions,
    dataSource: dataSourceTypes.conditions.id,
    propertyConfigs: conditionPropertyConfigs,
  },
  {
    title: labels.titles.medicationRequest,
    dataSource: dataSourceTypes.medicationRequest.id,
    propertyConfigs: medicationRequestPropertyConfigs,
  },
  {
    title: labels.titles.medicationAdministration,
    dataSource: dataSourceTypes.medicationAdministration.id,
    propertyConfigs: medicationAdministrationPropertyConfigs,
  },
  {
    title: labels.titles.medicationStatement,
    dataSource: dataSourceTypes.medicationStatement.id,
    propertyConfigs: medicationStatementPropertyConfigs,
  },
  {
    title: labels.titles.media,
    dataSource: dataSourceTypes.media.id,
    propertyConfigs: mediaPropertyConfigs,
  },
  {
    title: labels.titles.documentReference,
    dataSource: dataSourceTypes.documentReference.id,
    propertyConfigs: documentReferencePropertyConfigs,
  },
  {
    title: labels.titles.diagnosticReport,
    dataSource: dataSourceTypes.diagnosticReport.id,
    propertyConfigs: diagnosticReportPropertyConfigs,
  },
  {
    title: labels.titles.medicationDispense,
    dataSource: dataSourceTypes.medicationDispense.id,
    propertyConfigs: medicationDispensePropertyConfigs,
  },
  {
    title: labels.titles.referralRequest,
    dataSource: dataSourceTypes.referralRequest.id,
    propertyConfigs: referralRequestPropertyConfigs,
  },
  {
    title: labels.titles.specimen,
    dataSource: dataSourceTypes.specimen.id,
    propertyConfigs: specimenPropertyConfigs,
  },
  {
    title: labels.titles.imagingStudy,
    dataSource: dataSourceTypes.imagingStudy.id,
    propertyConfigs: imagingStudyPropertyConfigs,
  },
  {
    title: labels.titles.consent,
    dataSource: dataSourceTypes.consent.id,
    propertyConfigs: consentPropertyConfigs,
  },
  {
    title: labels.titles.procedureRequest,
    dataSource: dataSourceTypes.procedureRequest.id,
    propertyConfigs: procedureRequestPropertyConfigs,
  },
  {
    title: labels.titles.coverage,
    dataSource: dataSourceTypes.coverage.id,
    propertyConfigs: coveragePropertyConfigs,
  },
  {
    title: labels.titles.explanationOfBenefit,
    dataSource: dataSourceTypes.explanationOfBenefit.id,
    propertyConfigs: eobPropertyConfigs,
  },
  {
    title: labels.titles.immunization,
    dataSource: dataSourceTypes.medicationImmunization.id,
    propertyConfigs: immunizationPropertyConfigs,
  },
];

const dataSourceToConfig = (dataSource: DataSourceType) =>
  resourceConfigs.find(configItem => configItem.dataSource === dataSource);

/**
 * Gets all columns that are available for a given data source
 */
const getPropertyConfigSet = (
  dataSource: DataSourceType,
  parseConfiguration: ParseConfiguration = new ParseConfiguration(),
): PropertyConfig[] => {
  const dataSourceConfig = dataSourceToConfig(dataSource);
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return dataSourceConfig.propertyConfigs(parseConfiguration);
};

/**
 * Returns all columns that are availabel for a given datasource as a hashmap.  This allows faster lookup with a column key
 */
const getKeyedPropertyConfigSet = (
  dataSource: DataSourceType,
  parseConfiguration: ParseConfiguration = new ParseConfiguration(),
): { [key: string]: PropertyConfig } => {
  const propertyConfigs = getPropertyConfigSet(dataSource, parseConfiguration);

  const map: { [key: string]: PropertyConfig } = {};
  propertyConfigs.forEach(config => {
    map[config.key] = config;
  });

  return map;
};

export {
  observationPropertyConfigs,
  procedurePropertyConfigs,
  encounterPropertyConfigs,
  conditionPropertyConfigs,
  coveragePropertyConfigs,
  eobPropertyConfigs,
  immunizationPropertyConfigs,
  medicationRequestPropertyConfigs,
  medicationAdministrationPropertyConfigs,
  medicationStatementPropertyConfigs,
  mediaPropertyConfigs,
  documentReferencePropertyConfigs,
  diagnosticReportPropertyConfigs,
  medicationDispensePropertyConfigs,
  referralRequestPropertyConfigs,
  specimenPropertyConfigs,
  imagingStudyPropertyConfigs,
  consentPropertyConfigs,
  resourceAgnosticPropertyKeys,
  getPropertyConfigSet,
  getKeyedPropertyConfigSet,
  dataSourceToConfig,
};
